import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { AutomatedActionOffer } from '@core/automated-actions-types';
import { DepartmentSelectors } from '@core/department-data';
import { LocationsDescriptionHelper } from '@core/locations-logic';
import { JobItemsSelectors } from '@core/offers-data';
import { OfferSelectOption } from '@core/offers-ui';
import { TalentPoolsDataService } from '@core/talent-pools-data';

@Injectable({
  providedIn: 'root',
})
export class AssignActionDataFacade {
  private readonly store = inject(Store);
  private readonly talentPoolsDataService = inject(TalentPoolsDataService);
  private readonly locationsDescriptionHelper = inject(LocationsDescriptionHelper);

  private currentUser = this.store.selectSignal(CurrentUserSelectors.data);
  private hasViewAllJobsPermission = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ViewAllJobs),
  );
  private assignableJobs = this.store.selectSignal(JobItemsSelectors.canAssignTo);
  private assignableTalentPools = toSignal(this.talentPoolsDataService.collection$(), {
    initialValue: [],
  });
  private departmentsMap = this.store.selectSignal(DepartmentSelectors.entityMap);

  public getAvailableJobs(
    references: AutomatedActionOffer[],
    currentJobId?: number,
  ): OfferSelectOption[] {
    return [
      ...references,
      ...this.assignableJobs().filter(
        job => !references.some(reference => reference.id === job.id),
      ),
    ]
      .filter(job => job.id !== currentJobId)
      .map(job => ({
        id: job.id,
        title: job.title,
        status: job.status,
        kind: job.kind,
        locationNames: this.locationsDescriptionHelper.getLocationNamesValue(
          job?.locationIds || [],
        ),
        department: job.departmentId ? this.departmentsMap()?.[job.departmentId]?.name : null,
        disabled:
          !this.hasViewAllJobsPermission() &&
          !this.currentUser()?.membership?.offerIds?.includes(job.id),
      }));
  }

  public getAvailableTalentPools(references: AutomatedActionOffer[]): OfferSelectOption[] {
    return [
      ...references,
      ...this.assignableTalentPools().filter(
        tp => !references.some(reference => reference.id === tp.id),
      ),
    ].map(tp => ({
      id: tp.id,
      title: tp.title,
      status: tp.status,
      kind: tp.kind,
      disabled:
        !this.hasViewAllJobsPermission() &&
        !this.currentUser()?.membership?.talentPoolIds?.includes(tp.id),
    }));
  }
}
